import React, { useMemo } from 'react';
import ContainerBox from 'components/ContainerBox';
import { Category } from 'common/types';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useSelector } from 'react-redux';
import RentleDataGrid from 'components/datagrid/RentleDataGrid';
import CategoriesTableToolbar from './CategoriesTableToolbar';
import { push, Routes } from 'routing';
import { useTranslation } from 'services/localization';
import { categoryToCategoriesTableRow } from './rows';
import { getCategoriesTableColumns } from './columns';

import * as StockSelectors from 'selectors/StockSelectors';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
interface Props {
	categories: Category[];
	searchValue?: string;
	loading?: boolean;
}
const CategoriesTable = (props: Props) => {
	const { categories, searchValue, loading } = props;
	const { t } = useTranslation();
	const productsByCategory = useSelector(StockSelectors.stockProductsByCategory);
	const lang = useSelector(LanguageSelectors.language);

	const apiRef = useGridApiRef();

	const columns = useMemo(() => {
		return getCategoriesTableColumns({
			t,
		});
	}, [t]);

	const rows = useMemo(() => {
		const mapFunction = categoryToCategoriesTableRow({
			lang,
			productsByCategory,
		});
		return categories.map(mapFunction);
	}, [categories, lang, productsByCategory]);

	return (
		<ContainerBox padding="none">
			<RentleDataGrid
				apiRef={apiRef}
				rows={rows}
				columns={columns}
				autoHeight
				rowHeight={80}
				density="comfortable"
				components={{
					Toolbar: CategoriesTableToolbar,
				}}
				onRowClick={(params) => push(Routes.CategoryPage(params.id as string))}
				defaultSortModel={[
					{
						field: 'orderIndex',
						sort: 'asc',
					},
				]}
				loading={loading}
				componentsProps={{
					noRowsOverlay: {
						title: !!searchValue
							? t('CategoriesTable.noSearchResults', {
									searchValue,
									defaultValue: 'No matching categories found for "{{searchValue}}"',
							  })
							: t('CategoriesTable.noCategories', 'You have no categories'),
						body: !!searchValue
							? ''
							: t(
									'ProductCategories.emptyDescription',
									'Create categories to group together products in your shop and make it easier for your customers to find what they are looking for',
							  ),
					},
				}}
			/>
		</ContainerBox>
	);
};

export default CategoriesTable;
